import { useLocalization } from '@lib/localization';
import { has } from '@lib/permission';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField } from '@mui/material';
import { type CustomFolderChildRow } from '@pages/Projects';
import { useAddFolderMutation, useGetFoldersQuery, useUpdateFolderMutation, type IFolder } from '@store/api/folderEndpoints';
import { usePatchProjectMutation, type IProject } from '@store/api/projectEndpoints';
import React, { useEffect, useState } from 'react';
import { z } from 'zod';

interface FolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  folderItem?: CustomFolderChildRow | null;
  currentFolder?: IFolder | null;
}

const folderSchema = z.object({
  name: z.string()
    .min(1, 'Name must be at least 1 characters')
    .max(255, 'Folder name is too long')
    .regex(/^[^\\/:*?"<>|]+$/, 'Folder name cannot contain invalid characters (\\ / : * ? " < > |)'),
  parentFolder: z.string()
    .regex(/^[0-9a-fA-F]{24}$/, 'Invalid Parent Folder')
    .nullable()
    .optional()
}).strict();

type FieldErrorKeys = 'parentFolder' | 'name';
type FormErrors = { [K in FieldErrorKeys]?: string | null };

const FolderModal: React.FC<FolderModalProps> = ({ isOpen, onClose, folderItem, currentFolder }) => {
  const [parentFolder, setParentFolder] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [isUpdateForm, setUpdateForm] = useState<boolean>(false);
  const [isMoveProjectForm, setMoveProjectForm] = useState<boolean>(false);
  const { t } = useLocalization();

  const [addFolder, { isLoading: isAddFolderLoading }] = useAddFolderMutation();
  const [updateFolder, { isLoading: isUpdateFolderLoading }] = useUpdateFolderMutation();
  const [patchProject, { isLoading: isPatchProjectLoading }] = usePatchProjectMutation();
  const { data: folders, isLoading: isFoldersLoading } = useGetFoldersQuery();
  const isLoading = isAddFolderLoading || isUpdateFolderLoading || isPatchProjectLoading || isFoldersLoading;

  // Only offer destinations the user may put content into (mirrors the backend's destination
  // check on move). Each candidate comes from GET /v1/folders with its own permission list.
  // Note this is not `folder:put`: that asks whether the destination itself may be edited,
  // which team roots and the virtual root never allow, and would hide them from every user.
  const isAllowedDestination = (destination: IFolder): boolean => {
    if (!isUpdateForm) return true;

    return isMoveProjectForm ? has(destination, 'project:post') : has(destination, 'folder:post');
  };

  const [formErrors, setFormErrors] = useState<FormErrors>({ parentFolder: null, name: null });

  useEffect(() => {
    const isUpdateForm = !!folderItem;
    const isMoveForm = isUpdateForm && folderItem.type === 'Project';

    setUpdateForm(isUpdateForm);
    setMoveProjectForm(isMoveForm);

    const item = (folderItem?.item ?? null) as IFolder | IProject | null;

    if (!isUpdateForm) {
      setParentFolder(currentFolder?._id ?? 'root');
      setName('');
    } else {
      setParentFolder(item?.parentFolder ? (item.parentFolder as string) : 'root');
      setName(folderItem.name);
    }
  }, [currentFolder?._id, folderItem]);

  const handleAddFolderItem = async () => {
    if (isLoading) return;

    const validation = folderSchema.safeParse({ parentFolder: parentFolder == 'root' ? null : parentFolder, name });

    if (!validation.success) {
      const zodErrors = validation.error.formErrors.fieldErrors;
      const fieldErrors: FormErrors = { parentFolder: null, name: null };

      type ZodErrorKeys = keyof typeof zodErrors;
      (Object.keys(zodErrors) as ZodErrorKeys[]).forEach((key) => {
        if (zodErrors[key]) {
          fieldErrors[key] = zodErrors[key][0];
        }
      });

      setFormErrors(fieldErrors);
      return;
    }

    const result = await addFolder({ data: validation.data });

    if (!result.error) handleClose();
  };

  const handleUpdateFolderItem = async () => {
    if (isLoading || !folderItem) return;

    const validation = folderSchema.safeParse({ parentFolder: parentFolder == 'root' ? null : parentFolder, name });

    if (!validation.success) {
      const zodErrors = validation.error.formErrors.fieldErrors;
      const fieldErrors: FormErrors = { name: null, parentFolder: null };

      type ZodErrorKeys = keyof typeof zodErrors;
      (Object.keys(zodErrors) as ZodErrorKeys[]).forEach((key) => {
        if (zodErrors[key]) {
          fieldErrors[key] = zodErrors[key][0];
        }
      });

      setFormErrors(fieldErrors);
      return;
    }

    const result = !isMoveProjectForm
      ? await updateFolder({ _id: folderItem.id, ...validation.data })
      : await patchProject({ id: folderItem.id, parentFolder: validation.data.parentFolder });

    if (!result.error) handleClose();
  };

  const handleClose = () => {
    if (isLoading) return;

    clearForm();
    onClose();
  };

  const clearForm = () => {
    if (isUpdateForm) setParentFolder('');
    setName('');
    setFormErrors({ parentFolder: null, name: null });
  };

  return (
    <Dialog open={isOpen} onClose={handleClose}>
      <DialogTitle>{`${!isUpdateForm ? t('Folder_Add') : !isMoveProjectForm ? t('Folder_Edit') : t('Project_Move')}`}</DialogTitle>
      <DialogContent>
        <form>
          <TextField
            select
            fullWidth
            label={t('Folder_ParentFolder')}
            margin="normal"
            variant="outlined"
            value={parentFolder}
            onChange={(e) => setParentFolder(e.target.value)}
            error={!!formErrors.parentFolder}
            helperText={formErrors.parentFolder}
            disabled={!isUpdateForm || isLoading}
          >
            {folders?.length
              ? folders
                ?.filter(f =>
                  isMoveProjectForm || !folderItem || !f.path.startsWith((folderItem.item as IFolder).path ?? '~'))
                ?.filter(isAllowedDestination)
                ?.map(f => <MenuItem key={f._id ?? 'root'} value={f._id ?? 'root'}>{f.path}</MenuItem>)
              : (<MenuItem disabled>No folders available</MenuItem>)}
          </TextField>
          <TextField
            fullWidth
            label={t('Common_Name')}
            margin="normal"
            variant="outlined"
            placeholder="Enter name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!!formErrors.name}
            helperText={formErrors.name}
            disabled={isLoading || isMoveProjectForm}
          />
        </form>
      </DialogContent>
      <DialogActions>
        <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
          <div>
            <Button
              onClick={handleClose} color="primary" sx={{ mr: '12px' }} startIcon={<ClearIcon />} disabled={isLoading}>
              {t('Common_Cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={!isUpdateForm ? handleAddFolderItem : handleUpdateFolderItem}
              color="primary"
              startIcon={<CheckIcon />}
              disabled={isLoading}
            >
              {!isUpdateForm ? t('Common_Add') : t('Common_Save')}
            </Button>
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default FolderModal;
